/**
 * Pagina-adressen (slugs).
 *
 * Spiegelt `backend/websites/builder_schemas.py::normalize_slug` en de
 * gedeelde contractgrammatica (`public-page.ts::pageSlugSchema`). De backend
 * blijft de autoriteit; dit bestaat zodat de gebruiker meteen ziet wat er
 * gebeurt in plaats van pas na een mislukte opslag.
 */
import type { SitePage } from './types';

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Adressen die de Website Module zelf gebruikt voor haar runtime-routes
 * (`/development/*`, `/preview/*`). Een pagina daarop zou permanent
 * onbereikbaar zijn.
 */
export const RESERVED_SLUGS = ['development', 'preview'];

export interface SlugCheck {
  ok: boolean;
  /** Genormaliseerde waarde als `ok`. */
  value: string;
  error?: string;
}

/** Zet vrije invoer om naar een geldig adres, of legt uit waarom dat niet kan. */
export function normalizeSlug(raw: string): SlugCheck {
  let candidate = (raw || '').trim().toLowerCase();
  if (candidate === '') return { ok: true, value: '' };

  candidate = candidate.replace(/^\/+|\/+$/g, '');
  if (candidate.includes('/')) {
    return {
      ok: false,
      value: '',
      error: "Een pagina-adres kan geen '/' bevatten. Gebruik bijvoorbeeld 'over-ons'.",
    };
  }

  candidate = candidate.replace(/\s+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
  if (candidate === '') {
    return { ok: false, value: '', error: "Vul een geldig pagina-adres in, bijvoorbeeld 'over-ons'." };
  }
  if (!SLUG_PATTERN.test(candidate)) {
    return {
      ok: false,
      value: '',
      error: "Gebruik alleen kleine letters, cijfers en koppeltekens, bijvoorbeeld 'over-ons'.",
    };
  }
  if (RESERVED_SLUGS.includes(candidate)) {
    return {
      ok: false,
      value: '',
      error: `'${candidate}' is gereserveerd en kan niet als pagina-adres worden gebruikt.`,
    };
  }
  return { ok: true, value: candidate };
}

/**
 * Maakt van een paginatitel een bruikbaar adres. Alleen als startvoorstel --
 * de gebruiker mag het altijd overschrijven.
 */
export function slugFromTitle(title: string): string {
  const stripped = (title || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const check = normalizeSlug(stripped);
  return check.ok && check.value !== '' ? check.value : 'nieuwe-pagina';
}

/** Voegt `-2`, `-3`, ... toe tot het adres binnen deze website uniek is. */
export function uniqueSlug(base: string, pages: SitePage[], exceptPageId?: string): string {
  const taken = new Set(
    pages.filter((page) => page.pageId !== exceptPageId).map((page) => page.slug),
  );
  if (!taken.has(base)) return base;
  for (let index = 2; index < 500; index += 1) {
    const candidate = `${base}-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

/** Hoe het adres eruitziet voor de bezoeker. */
export function displayPath(slug: string): string {
  return slug === '' ? '/' : `/${slug}`;
}
